import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { HttpErrorService, SvcHttpClient } from 'projects/lib-shared-common/src/public-api';
import { CommonPagination } from '@solvace/core/models';
import { SvcSignalRService } from '@solvace/core/services/signal-r/svc-signal-r.service';
import { SvcSignalRHub } from '@solvace/core/services/signal-r/svc-signal-r-hub.enum';
import { BehaviorSubject, Subject, Subscription, catchError, finalize, forkJoin, map, tap, throwError } from 'rxjs';
import { FeedItem, FeedItemFromSignalR, SvcFeedNoticationType } from '../models/feed-item.model';
import { HubConnection } from '@microsoft/signalr';
import { AuthService } from 'projects/lib-shared-core/src/public-api';
import { Application } from '../models/applications.model';
import { TranslocoService } from '@ngneat/transloco';
import { SvcComment } from '../models/comment.model';

const DEFAULT_PAGE_SIZE = 30;

export interface SvcFeedResponse {
  feeds: FeedItem[];
  dataAreOver: boolean;
}

@Injectable({ providedIn: 'root' })
export class SvcFeedService extends SvcHttpClient {
  /** FEED PROPERTIES - BEGIN */
  private _feed = new BehaviorSubject<SvcFeedResponse>({
    feeds: [],
    dataAreOver: false,
  });
  public get feed$() {
    return this._feed.asObservable();
  }

  private _feedIsLoading = new BehaviorSubject<boolean>(true);
  public get feedIsLoading$() {
    return this._feedIsLoading.asObservable();
  }

  private _hasErrorFeed = new Subject<boolean>();
  hasErrorFeed$ = this._hasErrorFeed.asObservable();

  private _hasErrorTotalUnread = new Subject<boolean>();
  hasErrorTotalUnread$ = this._hasErrorTotalUnread.asObservable();

  private _feedPageIndex: number = 1;
  public get feedPageIndex() {
    return this._feedPageIndex;
  }

  private _feedPageSize: number = DEFAULT_PAGE_SIZE;
  public get feedPageSize() {
    return this._feedPageSize;
  }

  private _feedParameters: {
    filter?: string;
    applicationIds?: string[];
    startDate?: string;
    endDate?: string;
  } = {};
  private _feedSubscription: Subscription;

  private _recentlyChangedFeedIds: number[] = [];
  /** FEED PROPERTIES - END */

  /** TOTAL PROPERTIES - BEGIN */
  private _totalUnread = new BehaviorSubject<number>(null);
  public get totalUnread$() {
    return this._totalUnread.asObservable();
  }

  private _totalUnreadIsLoading = new BehaviorSubject<boolean>(true);
  public get totalUnreadIsLoading$() {
    return this._totalUnreadIsLoading.asObservable();
  }

  private _totalUnreadSubscription: Subscription;
  /** TOTAL PROPERTIES - END */

  /** SIGNAL R - BEGIN */
  private _feedHubConnection: HubConnection;
  private _newFeedReceived = new Subject<FeedItemFromSignalR>();
  public get newFeedReceived$() {
    return this._newFeedReceived.asObservable();
  }
  private _feedRemoved = new Subject<{ commentHeaderId: number }>();
  public get feedRemoved$() {
    return this._feedRemoved.asObservable();
  }
  /** SIGNAL R - END */

  constructor(
    protected _appConfig: AppEnvironmentConfig,
    protected _httpClient: HttpClient,
    protected _authService: AuthService,
    protected _translocoService: TranslocoService,
    private _httpErrorService: HttpErrorService,
    private _svcSignalRService: SvcSignalRService
  ) {
    super(_appConfig.APIs.apiUrlCommentManager, _httpClient);
  }

  /** FEED METHODS - BEGIN */
  public getFeed(
    params?: {
      filter?: string;
      applicationIds?: string[];
      startDate?: string;
      endDate?: string;
    },
    options?: {
      preservePrevParams?: boolean;
    }
  ) {
    params = params ? { ...params } : {};
    this._feedIsLoading.next(true);
    for (const key in params ?? {}) {
      const value = params[key];
      if (value == null || value === '' || (Array.isArray(value) && !value.length)) {
        delete params[key];
      }
    }
    this._feedParameters = {
      ...((options?.preservePrevParams ?? false) ? this._feedParameters : {}),
      ...params,
    };

    this._resetFeeds();

    this._feedSubscription?.unsubscribe();
    this._feedSubscription = this.get<CommonPagination<FeedItem[]>>('/Manager', {
      params: {
        ...this._feedParameters,
        pageSize: this._feedPageSize,
        pageIndex: this._feedPageIndex,
      },
    })
      .pipe(
        tap((response) => {
          this._feed.next({
            feeds: this.handleFeeds(response.data),
            dataAreOver: response.data.length < this.feedPageSize,
          });
          this._hasErrorFeed.next(false);
        }),
        catchError((error) => {
          this._hasErrorFeed.next(true);
          this._httpErrorService.showErrorInToast(error);
          return error;
        }),
        finalize(() => this._feedIsLoading.next(false))
      )
      .subscribe();
  }

  public feedNextPage() {
    if (!this._feedIsLoading.value && !this._feed.value.dataAreOver) {
      this._feedIsLoading.next(true);
      this._feedSubscription = this.get<CommonPagination<FeedItem[]>>('/Manager', {
        params: {
          ...this._feedParameters,
          pageSize: this._feedPageSize,
          pageIndex: ++this._feedPageIndex,
        },
      })
        .pipe(
          tap((response) => {
            this._feed.next({
              feeds: [...this._feed.value.feeds, ...this.handleFeeds(response.data)],
              dataAreOver: response.data.length < this.feedPageSize,
            });
          }),
          finalize(() => this._feedIsLoading.next(false))
        )
        .subscribe();
    }
  }

  private _resetFeeds() {
    this._feedPageIndex = 1;
    this._feedPageSize = DEFAULT_PAGE_SIZE;
    this._feed.next({
      feeds: [],
      dataAreOver: false,
    });
  }

  private handleFeeds(feeds: FeedItem[]) {
    return feeds.map((feed) => ({
      ...feed,
      title: feed.title?.replace(/&nbsp;|\u00A0/g, ' ').trim() ?? '',
      commentContent: feed.commentContent?.replace(/&nbsp;|\u00A0/g, ' ').trim() ?? '',
    }));
  }
  /** FEED METHODS - END */

  /** TOTAL METHODS - BEGIN */
  public getTotalUnread() {
    this._totalUnreadIsLoading.next(true);
    this._totalUnreadSubscription?.unsubscribe();
    this._totalUnreadSubscription = this.get<number>('/Manager/TotalNoRead')
      .pipe(
        tap((response) => {
          this._totalUnread.next(response);
          this._hasErrorTotalUnread.next(false);
        }),
        catchError((error) => {
          this._hasErrorTotalUnread.next(true);
          return error;
        }),
        finalize(() => this._totalUnreadIsLoading.next(false))
      )
      .subscribe();
  }
  /** TOTAL METHODS - END */

  public updateCommentedFeedToAtStartOfFeeds(comment: SvcComment, selectedFeedCommentId: number): FeedItem {
    if (!comment || !selectedFeedCommentId) return null;

    const currentFeeds = this._feed.value.feeds;
    const indexSelectedFeed = currentFeeds.findIndex((feed) => feed.commentId === selectedFeedCommentId);

    if (indexSelectedFeed === -1) return null;

    const selectedFeed = currentFeeds[indexSelectedFeed];
    const [updatedFeedByComment] = this.handleFeeds([this.adapterCommentToFeed(comment, selectedFeed)]);

    const pinnedFeeds = currentFeeds.filter((feed) => feed.isPinned ?? false);
    const unpinnedFeeds = currentFeeds.filter((feed) => !(feed.isPinned ?? false));
    const selectedPinnedIndex = pinnedFeeds.findIndex((feed) => feed.commentHeaderId === selectedFeed.commentHeaderId);

    if (selectedPinnedIndex >= 0) {
      pinnedFeeds.splice(selectedPinnedIndex, 1);
      pinnedFeeds.unshift(updatedFeedByComment);
    } else {
      const selectedUnpinnedIndex = unpinnedFeeds.findIndex((feed) => feed.commentHeaderId === selectedFeed.commentHeaderId);
      if (selectedUnpinnedIndex >= 0) {
        unpinnedFeeds.splice(selectedUnpinnedIndex, 1);
      }
      unpinnedFeeds.unshift(updatedFeedByComment);
    }

    const updatedFeeds = [...pinnedFeeds, ...unpinnedFeeds];

    this._feed.next({
      feeds: updatedFeeds,
      dataAreOver: this._feed.value.dataAreOver,
    });

    return updatedFeedByComment;
  }

  /** SIGNAL R - BEGIN */
  public startListenSignalR(): void {
    const instance = this._svcSignalRService.connectTo(SvcSignalRHub.CommentManager);
    instance.addListener(this.onListenEvent);
  }

  private onListenEvent = (method: string, feed: FeedItemFromSignalR): void => {
    if (method === 'CommentDeleted' || feed?.typeNotification === SvcFeedNoticationType.REGISTRY_DELETED) {
      const commentHeaderId = feed?.commentHeaderId;
      if (commentHeaderId == null) return;
      const allCurrentFeeds = this._feed.value.feeds;
      const newFeeds = allCurrentFeeds.filter((f) => f.commentHeaderId !== commentHeaderId);
      this._feed.next({
        feeds: newFeeds,
        dataAreOver: this._feed.value.dataAreOver,
      });
      this._feedRemoved.next({ commentHeaderId });
      this.getTotalUnread();
      return;
    }
    if (method !== 'CommentCreated') return;
    if (this._isRecentlyChangedFeedId(feed.commentHeaderId)) {
      this._removeRecentlyChangedFeedId(feed.commentHeaderId);
      return;
    }
    if ([SvcFeedNoticationType.PINNING].includes(feed.typeNotification)) {
      this.getFeed(null, { preservePrevParams: true });
      return;
    }

    const typeNotification = feed.typeNotification as SvcFeedNoticationType;
    if (typeNotification === SvcFeedNoticationType.REGISTRY_DELETED) {
      const allCurrentFeeds = this._feed.value.feeds;
      const feedIndex = allCurrentFeeds.findIndex((f) => f.commentHeaderId === feed.commentHeaderId);
      if (feedIndex >= 0) {
        const newFeeds = allCurrentFeeds.filter((f) => f.commentHeaderId !== feed.commentHeaderId);
        this._feed.next({
          feeds: newFeeds,
          dataAreOver: this._feed.value.dataAreOver,
        });
        this._newFeedReceived.next(feed);
        this.getTotalUnread();
      }
      return;
    }

    const allCurrentFeeds = this._feed.value.feeds;
    const feedIndex = allCurrentFeeds.findIndex((f) => f.commentHeaderId === feed.commentHeaderId);
    let pinnedCurrentFeeds = allCurrentFeeds.filter((f) => f.isPinned ?? false);
    let unpinnedCurrentFeeds = allCurrentFeeds.filter((f) => !(f.isPinned ?? false));
    let newFeeds: FeedItem[];
    feed = <FeedItemFromSignalR>this.handleFeeds([feed])[0];
    if (feedIndex >= 0) {
      if (feed.typeNotification === SvcFeedNoticationType.NEW_COMMENT) {
        const feedInList = allCurrentFeeds[feedIndex];
        if (feedInList.commentId > feed.commentId) {
          this.getFeed(null, { preservePrevParams: true });
          this.getTotalUnread();
          return;
        }
        if (feedInList.commentId === feed.commentId) return;
        const selectedPinnedIndex = pinnedCurrentFeeds.findIndex((f) => f.commentHeaderId === feed.commentHeaderId);
        pinnedCurrentFeeds = pinnedCurrentFeeds.filter((f) => f.commentHeaderId !== feed.commentHeaderId);
        unpinnedCurrentFeeds = unpinnedCurrentFeeds.filter((f) => f.commentHeaderId !== feed.commentHeaderId);
        if (selectedPinnedIndex >= 0) {
          newFeeds = [feed, ...pinnedCurrentFeeds, ...unpinnedCurrentFeeds];
        } else {
          newFeeds = [...pinnedCurrentFeeds, feed, ...unpinnedCurrentFeeds];
        }
      } else if (
        [SvcFeedNoticationType.PINNING, SvcFeedNoticationType.MARK_UN_READ, SvcFeedNoticationType.TITLE_UPDATED].includes(
          feed.typeNotification
        )
      ) {
        newFeeds = allCurrentFeeds.map((item) => {
          if (item.commentHeaderId === feed.commentHeaderId) {
            if (feed.typeNotification === SvcFeedNoticationType.MARK_UN_READ) item.isMarkedUnRead = feed.isMarkedUnRead;
            if (feed.typeNotification === SvcFeedNoticationType.PINNING) item.isPinned = feed.isPinned;
            if (feed.typeNotification === SvcFeedNoticationType.TITLE_UPDATED) {
              item.title = feed.title;
              item.subtitle = feed.subtitle;
            }
          }
          return item;
        });
      }
    } else if (feed.typeNotification === SvcFeedNoticationType.NEW_COMMENT) {
      this._feedPageSize++;
      newFeeds = [...pinnedCurrentFeeds, feed, ...unpinnedCurrentFeeds];
    }

    this._feed.next({
      // AB#75333 SignalR events for a feed outside the list (MARK_UN_READ, TITLE_UPDATED, PINNING) leave newFeeds unassigned.
      feeds: newFeeds ?? allCurrentFeeds,
      dataAreOver: this._feed.value.dataAreOver,
    });
    this._newFeedReceived.next(feed);
    this.getTotalUnread();
  }

  public stopListenSignalR(): void {
    const instance = this._svcSignalRService.getAliveInstanceFromHub(SvcSignalRHub.CommentManager);
    instance?.removeListener(this.onListenEvent);
  }
  /** SIGNAL R - END */

  /** OTHERS ENDPOINTS - BEGIN */
  public getFilters() {
    return forkJoin({
      applications: this.get<Application[]>('/Application').pipe(
        map((response) => {
          // AB#68767 — the API may return duplicated applications; keep one entry per id
          const uniqueApplications = [...new Map(response.map((app) => [app.id, app])).values()];
          return uniqueApplications.map((app) => ({
            ...app,
            description: this._translocoService.translate(app.description),
          }));
        })
      ),
    });
  }

  public getUserHasPermission(params: { commentHeaderId: number; registryId: number | string }) {
    const isString = typeof params.registryId === 'string';
    const path = `/Manager/${params.commentHeaderId}/` + (isString ? 'registryUId' : 'registryId') + `/${params.registryId}/userHasPermission`;
    return this.get<boolean>(path);
  }

  public markAsReadByCommentHeaderId(commentHeaderId: number) {
    return this.put<boolean>(`/Manager/${commentHeaderId}/MarkAsRead`, null);
  }

  public changeFeedPin(commentHeaderId: number, params: { value: boolean }) {
    this._addRecentlyChangedFeedId(commentHeaderId);
    return this.post<boolean>(`/Manager/${commentHeaderId}/PinFeed`, null, {
      params: {
        isPinned: params.value,
      },
    }).pipe(
      catchError((error) => {
        this._removeRecentlyChangedFeedId(commentHeaderId);
        return throwError(() => error);
      })
    );
  }

  public markFeedAsUnread(commentHeaderId: number) {
    this._addRecentlyChangedFeedId(commentHeaderId);
    return this.post<boolean>(`/Manager/${commentHeaderId}/MarkFeedUnRead`, null, {
      params: {
        isMarkedUnRead: true,
      },
    }).pipe(
      catchError((error) => {
        this._removeRecentlyChangedFeedId(commentHeaderId);
        return throwError(() => error);
      })
    );
  }

  public markFeedAsRead(commentHeaderId: number) {
    this._addRecentlyChangedFeedId(commentHeaderId);
    return this.post<boolean>(`/Manager/${commentHeaderId}/MarkFeedUnRead`, null, {
      params: {
        isMarkedUnRead: false,
      },
    }).pipe(
      catchError((error) => {
        this._removeRecentlyChangedFeedId(commentHeaderId);
        return throwError(() => error);
      })
    );
  }

  private _isRecentlyChangedFeedId(feedId: number) {
    return this._recentlyChangedFeedIds.includes(feedId);
  }

  private _addRecentlyChangedFeedId(feedId: number) {
    if (!this._recentlyChangedFeedIds.includes(feedId)) {
      this._recentlyChangedFeedIds.push(feedId);
    }
  }

  private _removeRecentlyChangedFeedId(feedId: number) {
    this._recentlyChangedFeedIds = this._recentlyChangedFeedIds.filter((id) => id !== feedId);
  }

  private adapterCommentToFeed(comment: SvcComment, feed: FeedItem): FeedItem {
    return {
      ...feed,
      commentId: comment.commentId,
      commentContent: comment.commentText,
      userName: comment.user.name,
      userPictureFile: comment.user.picture,
      initialColor: comment.user.color,
      hasAttachment: comment.attachments.length > 0,
    };
  }
  /** OTHERS ENDPOINTS - END */
}
